import { FC, useState } from 'react';
import { PublishedStatusEnum, ValidStatusEnum } from '../../../types/key-task-summarizer-type';
import { appendAlertItem, AlertType } from '@pruforce/common-adminweb-sdk';
import { Alert } from '@mui/material';
import { RouteComponentProps } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { ModulePermissionProps } from 'src/app/common/module/types';
import { useDataProvider } from 'src/app/common/utils';
import { KEY_TASK_SUMMARIZER_BASE_PATH, OPERATION_BASE_PATH } from '../../../constants';
import { KeyTaskListParam, KeyTaskRecord } from '../../../types/key-task-summarizer-type';
import KeyTaskSummarizerList from './components/KeyTaskSummarizerList';
import KeyTaskSummarizerFilter from './components/KeyTaskSummarizerFilter';
import { fetchKeyTaskSummarizerList, updateKeyTaskSummarizer } from '../../../network/keyTaskSummarizerCrud';
import { PaginateList } from 'src/app/common/types/common-types';

type KeyTaskSummarizerListingPageProps = RouteComponentProps & ModulePermissionProps;

const initialState: KeyTaskListParam = {
  name: '',
  publishedStartDate: null,
  publishedEndDate: null,
  category: 'all',
  publishedStatus: PublishedStatusEnum.ALL,

  validStatus: ValidStatusEnum.ALL,
  page: 1,
  limit: 20,
  pagination: true,
};

const KeyTaskSummarizerListingPage: FC<KeyTaskSummarizerListingPageProps> = ({
  history,
  enableRead,
  enableCreate,
  enableUpdate,
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const Translation = (id: string) => intl.formatMessage({ id });
  const [filterSavedState, setFilterSavedState] = useState<KeyTaskListParam>(initialState);
  const [filterKey, setFilterKey] = useState(0);
  const [keyTaskList, setKeyTaskList] = useState<PaginateList<KeyTaskRecord>>();
  const [isUnpublishing, setIsUnpublishing] = useState(false);
  const canRead = enableRead || enableCreate || enableUpdate;
  const KEY_TASK_PATH = OPERATION_BASE_PATH + KEY_TASK_SUMMARIZER_BASE_PATH;

  const { isLoading, error, refreshData } = useDataProvider<PaginateList<KeyTaskRecord>>(
    async () => {
      if (!canRead) return;
      return fetchKeyTaskSummarizerList(filterSavedState, dispatch);
    },
    setKeyTaskList,
    false,
    undefined,
    !canRead,
  );

  const onUnpublish = async (row: KeyTaskRecord) => {
    if (!enableUpdate || isUnpublishing || row.publishedStatus !== PublishedStatusEnum.PUBLISHED) return;
    setIsUnpublishing(true);
    try {
      await updateKeyTaskSummarizer(row.id, { publishedStatus: PublishedStatusEnum.UNPUBLISHED });
      dispatch(
        appendAlertItem([
          {
            severity: AlertType.SUCCESS,
            title: Translation('global.submit.success'),
            content: Translation('global.submit.updated'),
          },
        ]),
      );
      refreshData();
    } catch {
      dispatch(
        appendAlertItem([
          {
            severity: AlertType.ERROR,
            title: Translation('global.submit.fail'),
            content: Translation('global.submit.fail'),
          },
        ]),
      );
    } finally {
      setIsUnpublishing(false);
    }
  };

  if (!canRead) return <Alert severity="error">{Translation('KeyTaskSummerizer.no_access')}</Alert>;

  return (
    <>
      <KeyTaskSummarizerFilter
        key={filterKey}
        filterState={filterSavedState}
        onChangeFilter={setFilterSavedState}
        onSearch={refreshData}
      />
      <KeyTaskSummarizerList
        isLoading={isLoading || isUnpublishing}
        keyTaskList={keyTaskList?.docs || []}
        totalRecords={keyTaskList?.totalDocs || 0}
        page={filterSavedState.page}
        limit={filterSavedState.limit}
        enableCreate={enableCreate}
        enableUpdate={enableUpdate}
        noRecordMsg={Translation(error ? 'common.loading.refreshAgain' : 'prutable.noRecord')}
        onRefresh={() => {
          setFilterKey((key) => key + 1);
          refreshData();
        }}
        onAdd={() => history.push(`${KEY_TASK_PATH}/create/portal`)}
        onEdit={(id) => history.push(`${KEY_TASK_PATH}/edit/${id}`)}
        onView={(id) => history.push(`${KEY_TASK_PATH}/view/${id}`)}
        onUnpublish={onUnpublish}
        onChangePage={(page, limit) => {
          setFilterSavedState((prev) => ({
            ...prev,
            page,
            limit,
          }));
          refreshData();
        }}
      />
    </>
  );
};

export default KeyTaskSummarizerListingPage;
